import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";

const ROLE_NOTE = {
  owner: "Full rights — settings, deletions, revising settled fee notes, API keys.",
  clerk: "Raises and sends fee notes, records payments. No settings, no deletions.",
};

export const AccessPanel = () => {
  const [grants, setGrants] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("clerk");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/access");
      setGrants(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load the sign-in list"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!email.trim()) {
      toast.error("Enter the Google address that should be allowed in");
      return;
    }
    setBusy(true);
    try {
      await api.post("/access", { email: email.trim(), role });
      setEmail("");
      toast.success("Added to the sign-in list");
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not add that address"));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (grant, next) => {
    try {
      await api.put(`/access/${grant.grant_id}`, { role: next });
      toast.success(`${grant.email} is now ${next === "owner" ? "an owner" : "a clerk"}`);
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not change those rights"));
    }
  };

  const remove = async (grant) => {
    if (!window.confirm(`Remove ${grant.email}? They will be signed out at once.`)) return;
    try {
      await api.delete(`/access/${grant.grant_id}`);
      toast.success("Removed");
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove that address"));
    }
  };

  return (
    <section className="surface p-6" data-testid="settings-access">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-terracotta" strokeWidth={1.8} />
        <div>
          <h2 className="font-serif text-xl text-slate-900">Who can sign in</h2>
          <p className="mt-2 max-w-2xl text-[12px] leading-relaxed text-slate-500">
            Only these Google addresses can open these chambers. Anyone else is turned away at the
            door and no account is created for them. A clerk seat works inside your chambers on the
            same ledger, with limited rights.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          type="email"
          placeholder="name@gmail.com"
          className="rounded-sm sm:max-w-xs"
          data-testid="access-email-input"
        />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="rounded-sm sm:w-44" data-testid="access-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="clerk">Clerk seat</SelectItem>
            <SelectItem value="owner">Owner</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={add}
          disabled={busy}
          data-testid="add-access-button"
          className="inline-flex items-center justify-center gap-2 bg-slate-900 px-5 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Allow this address
        </button>
      </div>

      <ul className="mt-6 divide-y divide-slate-100 border-y border-slate-100" data-testid="access-list">
        {grants.map((grant) => (
          <li
            key={grant.grant_id}
            className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`access-row-${grant.email}`}
          >
            <div className="min-w-0">
              <p className="truncate text-[13px] text-slate-900">
                {grant.email}
                {grant.is_you ? <span className="text-slate-400"> · you</span> : null}
              </p>
              <p className="truncate text-[11px] text-slate-400">
                {grant.last_login_at
                  ? `last signed in ${grant.last_login_at.slice(0, 10)}`
                  : "never signed in"}
                {" · "}
                {ROLE_NOTE[grant.role]}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                disabled={grant.is_you}
                onClick={() => changeRole(grant, grant.role === "owner" ? "clerk" : "owner")}
                data-testid={`access-role-toggle-${grant.email}`}
                className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors duration-150 disabled:opacity-40 ${
                  grant.role === "owner"
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 text-slate-600 hover:border-slate-900"
                }`}
              >
                <UserCog className="h-3 w-3" strokeWidth={2} />
                {grant.role === "owner" ? "Owner" : "Clerk"}
              </button>
              <button
                type="button"
                disabled={grant.is_you}
                onClick={() => remove(grant)}
                data-testid={`remove-access-${grant.email}`}
                className="border border-slate-200 p-2 text-slate-400 transition-colors duration-150 hover:border-red-300 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
        Tap the role to switch a seat between owner and clerk. You cannot change or remove your own
        access, and these chambers always keep at least one owner.
      </p>
    </section>
  );
};
